// ==============================================================================
// VERSION INFORMATION
// ==============================================================================
// version = "1.1.0"
//
// Changelog:
//
// Version 1.1.0:
// - [Added] An optional `referral_code` field to the `CoachRegisterPayload` to
//   support the new affiliate program feature.
// ==============================================================================
// File: src/services/authService.ts
import apiClient from './api';
import { TokenResponse, User, MsgResponse } from '../types';
import { TOKEN_KEY, REFRESH_TOKEN_KEY } from '../config'; 

export interface LoginCredentials {
  username: string; 
  password: string;
}

export interface RegisterByInvitationPayload {
  email: string;
  password: string;
  first_name?: string | null;
  last_name?: string | null;
  invitation_token: string;
}

export interface CoachRegisterPayload {
  email: string;
  password: string;
  first_name?: string | null;
  last_name?: string | null;
  timezone?: string | null;
  referral_code?: string;
}


export interface PasswordResetConfirmPayload {
  token: string;
  new_password: string;
}

export interface CurrentUserUpdatePayload {
  email?: string;
  password?: string; 
  first_name?: string | null;
  last_name?: string | null;
  profile_picture_url?: string | null;
  timezone?: string | null;
}

// NEW: Payload for resending verification email
export interface ResendVerificationRequestPayload {
  email: string;
}

const login = async (credentials: LoginCredentials): Promise<TokenResponse> => {
  const formData = new URLSearchParams();
  formData.append('username', credentials.username);
  formData.append('password', credentials.password);

  const { data } = await apiClient.post<TokenResponse>('/auth/login/access-token', formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });

  localStorage.setItem(TOKEN_KEY, data.access_token);
  if (data.refresh_token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
  }

  return data;
};

const getCurrentUser = async (): Promise<User> => {
  const { data } = await apiClient.get<User>('/auth/me');
  return data;
};

const updateCurrentUser = async (payload: CurrentUserUpdatePayload): Promise<User> => {
  const { data } = await apiClient.put<User>('/auth/me', payload);
  return data;
};

const registerByInvitation = async (payload: RegisterByInvitationPayload): Promise<User> => {
  const { data } = await apiClient.post<User>('/auth/register/by-invitation', payload);
  return data;
};

const registerCoach = async (payload: CoachRegisterPayload): Promise<MsgResponse> => {
  const { data } = await apiClient.post<MsgResponse>('/auth/register/coach', payload);
  return data;
};

const verifyCoachEmail = async (token: string): Promise<MsgResponse> => {
    const { data } = await apiClient.post<MsgResponse>('/auth/verify-coach-email', { token });
    return data;
};

// NEW: Function to resend verification email
const resendVerificationEmail = async (payload: ResendVerificationRequestPayload): Promise<MsgResponse> => {
  const { data } = await apiClient.post<MsgResponse>('/auth/resend-verification-email', payload);
  return data;
};

const requestPasswordRecovery = async (email: string): Promise<MsgResponse> => {
  const { data } = await apiClient.post<MsgResponse>(`/auth/password-recovery/${email}`);
  return data;
};

const resetPassword = async (payload: PasswordResetConfirmPayload): Promise<MsgResponse> => {
  const { data } = await apiClient.post<MsgResponse>('/auth/reset-password/', payload);
  return data;
};

// NEW: Function to upload a profile picture
const uploadProfilePicture = async (file: File): Promise<User> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.put<User>('/users/me/profile-picture', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};


export default {
  login,
  getCurrentUser,
  updateCurrentUser,
  registerByInvitation,
  registerCoach,
  verifyCoachEmail,
  resendVerificationEmail, // Export new function
  requestPasswordRecovery,
  resetPassword,
  uploadProfilePicture,
};